import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import EventCover from "@/src/components/events/event-details/event-cover";
import EventDetailInfo from "@/src/components/events/event-details/event-info";
import EventDetailLocation from "@/src/components/events/event-details/event-location";
import EventParticipants from "@/src/components/events/event-details/event-participants";
import EventDetailPhotos from "@/src/components/events/event-details/event-photos";
import AttachmentViewer, {
  Attachment,
} from "@/src/components/ui-kit/attachment-viewer";
import { useAuth } from "@/src/contexts/auth.context";
import { useEventArchive } from "@/src/hooks/features/events/use-event-archive.hooks";
import { useEventRSVP } from "@/src/hooks/features/events/use-event-rsvp.hook";
import {
  EventItem,
  RSVPStatus,
  getMeetingUrl,
} from "@/src/models/event.model";
import { useGetCategoriesQuery } from "@/src/services/api/category/category.hooks";
import {
  useDeleteEventMutation,
  useGetRSVPsQuery,
} from "@/src/services/api/events/events.hooks";
import { isImageFile } from "@/src/utils/attachment.utils";
import { router } from "expo-router";
import { useCallback, useState } from "react";
import GuestEntryModal from "@/src/components/events/guest-entry-modal";
import { useTranslation } from "react-i18next";
import { Linking, ScrollView, Text, TouchableOpacity, View } from "react-native";
import BackNavigationButton from "@/src/components/ui-kit/back-navigation";
import ScButton from "@/src/components/ui-kit/button";
type EventDetailProps = Readonly<{
  event: EventItem;
  onBack: () => void;
}>;

const MAX_VISIBLE_AVATARS = 3;

const EventDetail = ({ event, onBack }: EventDetailProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showParticipants, setShowParticipants] = useState(false);

  const [eventToDelete, setEventToDelete] = useState<EventItem | null>(null);
  const { data: categoriesData } = useGetCategoriesQuery({
    tag: "events",
    pageSize: 1000,
  });

  const { data: rsvpsData } = useGetRSVPsQuery(event.ItemId, RSVPStatus.GOING);

  const {
    userRsvp,
    isAttending,
    isWaitlisted,
    isJoining,
    isLeaving,
    joinButtonText,
    handleJoin,
    handleLeave,
    seatsTaken,
  } = useEventRSVP(event);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);

  const handleJoinLeave = () => {
    if (isAttending || isWaitlisted) {
      setShowLeaveModal(true);
    } else {
      // Ask about guests before joining. Confirming with no names is a normal
      // solo join — the modal is not a blocker.
      setShowGuestModal(true);
    }
  };

  const handleConfirmGuests = (guestNames: string[]) => {
    setShowGuestModal(false);
    handleJoin(guestNames);
  };

  const handleConfirmLeave = () => {
    setShowLeaveModal(false);
    if (!userRsvp) return;
    handleLeave(userRsvp.ItemId);
  };

  const categories =
    categoriesData?.items
      .filter((category) => event.categoryIds?.includes(category.ItemId))
      ?.map((category) => category.categoryName) ?? [];

  // Old RSVP rows survive an edit that turns registration off, so gate the
  // participant count / avatars on the current policy — otherwise a "None"
  // event still shows "1 person joined" and a pill that opens nothing.
  const hasParticipants = event.registrationPolicy !== "None";
  // Use the query's totalCount, not items.length — the RSVP list is paged at
  // 100, so a larger event would otherwise stall the headline at "100 joined".
  const attendingCount = hasParticipants
    ? (rsvpsData?.totalCount ?? rsvpsData?.items?.length ?? 0)
    : 0;

  const { handleArchiveEvent, isEventArchived } = useEventArchive();
  const isArchived = isEventArchived(event.ItemId);

  const now = new Date();
  const coverImage = event.coverImage ?? undefined;
  const visibilityLabel = event.visibility ?? undefined;
  const capacity = event.capacity ?? 0;
  // Online events resolve to a meeting URL; in-person ones do not.
  const meetingUrl = getMeetingUrl(event);
  const attachments: Attachment[] = (event.attachmentUrls ?? []).map((url) => ({
    url,
    type: isImageFile(url) ? "image" : "file",
  }));
  // Gated on `hasParticipants` for the reason above — stale rows outlive an edit
  // that turns registration off. Within an event that *does* take registrations,
  // a participant with no picture still belongs in the cluster: requiring
  // `profileImageUrl` hid them, and on an event where nobody had uploaded one
  // the cluster disappeared entirely, taking the only way into the participant
  // list with it. BlocksAvatar renders initials when there is no image.
  const visibleAvatars = (hasParticipants ? (rsvpsData?.items ?? []) : [])
    .slice(0, MAX_VISIBLE_AVATARS)
    .flatMap((rsvp) =>
      rsvp.CreatedBy
        ? [
            {
              userId: rsvp.CreatedBy,
              profileImageUrl:
                rsvp.eventParticipantSnapshot?.profileImageUrl ?? undefined,
              firstName: rsvp.eventParticipantSnapshot?.firstName,
              lastName: rsvp.eventParticipantSnapshot?.lastName,
            },
          ]
        : [],
    );
  const isOngoing =
    new Date(event.startTime) <= now && new Date(event.endTime) >= now;
  const isUpcoming = new Date(event.startTime) > now;
  const isCurrentUserAuthor = event.CreatedBy === user?.itemId;
  const showJoin = isUpcoming && event.registrationPolicy === "Required";

  const { mutate: deleteEvent, isPending: isDeleting } =
    useDeleteEventMutation();

  const handleEditEvent = useCallback((event: EventItem) => {
    router.push({
      pathname: "/events/[id]/edit",
      params: { id: event.ItemId },
    });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!eventToDelete) return;
    deleteEvent(
      { itemId: eventToDelete.ItemId, entity: eventToDelete },
      {
      onSuccess: () => {
        setEventToDelete(null);
        router.replace("/events");
      },
    });
  }, [eventToDelete, deleteEvent]);
  return (
    <ScrollView
      className="flex-1"
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ flexGrow: 1 }}
    >
      <View className="rounded-2xl border border-ash-3 bg-chalk overflow-hidden p-4 gap-4">
        <BackNavigationButton
          label={t("LABEL_BACK")}
          iconSize={18}
          iconClassName="text-onyx"
          onPress={onBack}
          containerStyle={{ marginBottom: 0, alignSelf: "flex-start" }}
        />

        <EventCover
          uri={coverImage}
          title={event.title}
          visibilityLabel={visibilityLabel}
          categories={categories}
          isAuthor={event.CreatedBy === user?.itemId}
          author={event.eventOrganizerSnapshot ?? undefined}
          authorId={event.CreatedBy}
          attendingCount={attendingCount}
          visibleAvatars={visibleAvatars}
          isOngoing={isOngoing}
          isArchived={isArchived}
          showJoin={showJoin}
          onJoin={handleJoinLeave}
          joinButtonText={joinButtonText}
          isJoiningOrLeaving={isJoining || isLeaving}
          // Registration-enabled events always get a working handler, even
          // before anyone joins: the cluster is the only route into the
          // participant panel, which is where an invite-only author invites
          // people. A "None" event passes nothing, so no cluster renders.
          onParticipantsPress={
            hasParticipants ? () => setShowParticipants(true) : undefined
          }
          onEdit={() => handleEditEvent(event)}
          onArchive={() => handleArchiveEvent(event)}
          onDelete={() => setEventToDelete(event)}
          isAttending={isAttending}
          isWaitlisted={isWaitlisted}
        />

        {/* The "Not Going" control and its confirm modal are gone: the cover
            button already reads "Leave" once attending, so offering both was two
            paths to one outcome. `handleDecline` remains on the RSVP hook if a
            future change needs the Declined status again. */}

        {showParticipants && hasParticipants ? (
          <EventParticipants
            event={event}
            isAuthor={isCurrentUserAuthor}
            onClose={() => setShowParticipants(false)}
          />
        ) : (
          <>
            <EventDetailInfo
              description={event.description}
              startTime={event.startTime}
              endTime={event.endTime}
              // Seats (attendees + guests), not the plain attendee count — this
              // is what capacity is measured in, so the bar and the join
              // button's "full" state stay in agreement.
              seatsTaken={seatsTaken}
              capacity={capacity}
              registrationPolicy={event.registrationPolicy}
            />
            {/* CR-B2-10: online events show a join-meeting link; in-person show the venue */}
            {meetingUrl ? (
              // Show the URL itself, not just the button — attendees need to copy it
              // into a calendar invite or open it on another device, which a bare
              // "Join" button does not allow.
              <View className="gap-2">
                <TouchableOpacity
                  className="flex-row items-center justify-center gap-2 bg-primary-light rounded-xl px-4 py-3"
                  onPress={() => Linking.openURL(meetingUrl).catch(() => {})}
                >
                  <Text className="text-primary font-semibold text-sm">
                    {t("LABEL_JOIN_MEETING")}
                  </Text>
                </TouchableOpacity>
                <Text
                  className="text-xs text-ash-2 px-1"
                  selectable
                  numberOfLines={2}
                  testID="event-meeting-url"
                >
                  {meetingUrl}
                </Text>
              </View>
            ) : (
              event.locationName && (
                <EventDetailLocation location={event.locationName} />
              )
            )}
            {attachments.length > 0 && (
              <AttachmentViewer attachments={attachments} />
            )}
            {!isUpcoming && !isOngoing && (
              <EventDetailPhotos
                eventId={event.ItemId}
                isOrganizer={isCurrentUserAuthor}
              />
            )}
          </>
        )}
      </View>
      <ConfirmModal
        visible={!!eventToDelete}
        title="LABEL_DELETE_EVENT"
        description="QUEST_DELETE_EVENT"
        confirmText={t("BTN_DELETE")}
        cancelText={t("BTN_CANCEL")}
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setEventToDelete(null)}
      />
      <ConfirmModal
        visible={showLeaveModal}
        title={
          isWaitlisted
            ? "LABEL_LEAVE_WAITLIST_CONFIRM"
            : "LABEL_LEAVE_EVENT_CONFIRM"
        }
        description={
          isWaitlisted
            ? "HINT_LEAVE_WAITLIST_CONFIRM"
            : "HINT_LEAVE_EVENT_CONFIRM"
        }
        confirmText={t(isWaitlisted ? "BTN_LEAVE_WAITLIST" : "BTN_LEAVE")}
        cancelText={t("BTN_CANCEL")}
        onCancel={() => setShowLeaveModal(false)}
        onConfirm={handleConfirmLeave}
      />
      {/* CR-B2-08: decline confirmation */}
      <GuestEntryModal
        visible={showGuestModal}
        isSubmitting={isJoining}
        onCancel={() => setShowGuestModal(false)}
        onConfirm={handleConfirmGuests}
      />

    </ScrollView>
  );
};

export default EventDetail;
